import React, { useState, useRef, useEffect, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Icons } from "./Machine360Icons";

export const Machine360Sidebar = ({
  sidebarData,
  isLoading = false,
  versionList = [],
  activeMachineId = null,
  currentVersionNumber = "",
  isVersionLoading = false,
  onVersionChange,
}) => {
  const [isVersionOpen, setIsVersionOpen] = useState(false);
  const versionDropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        versionDropdownRef.current &&
        !versionDropdownRef.current.contains(event.target)
      ) {
        setIsVersionOpen(false);
      }
    };
    if (isVersionOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVersionOpen]);

  const displayVersions = useMemo(() => {
    if (Array.isArray(versionList) && versionList.length > 0) {
      return versionList;
    }
    if (currentVersionNumber || activeMachineId) {
      return [
        {
          machineid: activeMachineId,
          versionnumber: currentVersionNumber || "1.0.0",
          status: "Active",
        },
      ];
    }
    return [];
  }, [versionList, activeMachineId, currentVersionNumber]);

  const activeVersionDisplay = useMemo(() => {
    const matched = displayVersions.find(
      (v) => String(v.machineid) === String(activeMachineId)
    );
    const ver = matched?.versionnumber || currentVersionNumber || "1.0.0";
    return String(ver).toLowerCase().startsWith("v") ? String(ver) : `v${ver}`;
  }, [displayVersions, activeMachineId, currentVersionNumber]);

  if (isLoading) {
    return (
      <aside className="m360-sidebar-card">
        {/* Header Skeleton */}
        <div className="m360-sidebar-header">
          <Skeleton height={20} width={130} />
          <Skeleton height={24} width={55} borderRadius={12} />
        </div>

        <div className="m360-sidebar-body">
          {/* Classification Skeleton */}
          <div className="m360-sidebar-section">
          <Skeleton height={14} width={90} />
          <div className="m360-detail-row mt-2">
            <Skeleton height={14} width={65} />
            <Skeleton height={14} width={85} />
          </div>
        </div>

        {/* Categories Skeleton */}
        <div className="m360-sidebar-section">
          <Skeleton height={14} width={75} />
          <div className="m360-categories-list mt-2">
            <Skeleton height={24} width={90} borderRadius={12} />
          </div>
        </div>

        {/* Sub Categories Skeleton */}
        <div className="m360-sidebar-section">
          <Skeleton height={14} width={95} />
          <div className="mt-2">
            <Skeleton height={14} width={45} />
          </div>
        </div>

        {/* Compatibility & Dependencies Skeleton */}
        <div className="m360-sidebar-section">
          <Skeleton height={14} width={160} />
          <div className="m360-detail-row mt-2">
            <Skeleton height={14} width={130} />
            <Skeleton height={14} width={30} />
          </div>
          <div className="m360-detail-row">
            <Skeleton height={14} width={130} />
            <Skeleton height={14} width={30} />
          </div>
          <div className="m360-detail-row">
            <Skeleton height={14} width={55} />
            <Skeleton height={14} width={40} />
          </div>
        </div>

        {/* Data Files Skeleton */}
        <div className="m360-sidebar-section">
          <Skeleton height={14} width={75} />
          <div className="m360-data-file-item mt-2">
            <div className="m360-data-file-top">
              <Skeleton height={14} width={75} />
              <Skeleton height={24} width={75} borderRadius={4} />
            </div>
            <div className="mt-1">
              <Skeleton height={12} width="90%" />
            </div>
          </div>
          <div className="m360-data-file-item">
            <div className="m360-data-file-top">
              <Skeleton height={14} width={85} />
              <Skeleton height={24} width={75} borderRadius={4} />
            </div>
            <div className="mt-1">
              <Skeleton height={12} width="90%" />
            </div>
          </div>
        </div>

        {/* Infrastructure Requirement Skeleton */}
        <div className="m360-infra-card">
          <Skeleton height={16} width={160} />
          <div className="m360-infra-row mt-2">
            <Skeleton height={14} width={110} />
            <Skeleton height={14} width={45} />
          </div>
          <div className="m360-infra-row">
            <Skeleton height={14} width={45} />
            <Skeleton height={14} width={40} />
          </div>
          <div className="m360-infra-row">
            <Skeleton height={14} width={60} />
            <Skeleton height={14} width={40} />
          </div>
          <div className="m360-infra-row">
            <Skeleton height={14} width={50} />
            <Skeleton height={14} width={40} />
          </div>
        </div>

        {/* Usage Stats Skeleton */}
        <div className="m360-sidebar-section m360-usage-stats-section">
          <Skeleton height={14} width={85} />
          <div className="m360-detail-row mt-2">
            <Skeleton height={14} width={110} />
            <Skeleton height={14} width={30} />
          </div>
          <div className="m360-detail-row">
            <Skeleton height={14} width={120} />
            <Skeleton height={14} width={30} />
          </div>
        </div>
      </div>
    </aside>
    );
  }

  if (!sidebarData) return null;

  return (
    <aside className="m360-sidebar-card">
      {/* Header */}
      <div className="m360-sidebar-header">
        <h3 className="m360-sidebar-title">Machine Details</h3>
        <div className="m360-version-chip-container" ref={versionDropdownRef}>
          <button
            type="button"
            className={`m360-version-chip-btn ${isVersionOpen ? "open" : ""}`}
            onClick={() => setIsVersionOpen((prev) => !prev)}
            aria-label="Select Machine Version"
            aria-expanded={isVersionOpen}
          >
            <span className="m360-version-chip-text">{activeVersionDisplay}</span>
            <Icons.ChevronDown
              size={11}
              className={`m360-version-chip-arrow ${isVersionOpen ? "rotated" : ""}`}
            />
          </button>

          {isVersionOpen && (
            <div className="m360-version-dropdown-menu">
              <div className="m360-version-dropdown-header">
                <span>Versions</span>
                {isVersionLoading && (
                  <span className="m360-version-loading-badge">Updating...</span>
                )}
              </div>
              <div className="m360-version-dropdown-list">
                {displayVersions.map((item, idx) => {
                  const isSelected =
                    String(item.machineid) === String(activeMachineId);
                  const rawVer = item.versionnumber || item.number || "1.0.0";
                  const itemVerLabel = String(rawVer).toLowerCase().startsWith("v")
                    ? String(rawVer)
                    : `v${rawVer}`;

                  return (
                    <button
                      key={item.machineid || idx}
                      type="button"
                      className={`m360-version-item ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        if (onVersionChange && !isSelected) {
                          onVersionChange(item);
                        }
                        setIsVersionOpen(false);
                      }}
                    >
                      <div className="m360-version-item-left">
                        <span className="m360-version-item-num">{itemVerLabel}</span>
                        {item.isdiscontinued ? (
                          <span className="m360-ver-badge discontinued">Discontinued</span>
                        ) : item.isdeprecated ? (
                          <span className="m360-ver-badge deprecated">Deprecated</span>
                        ) : item.status ? (
                          <span
                            className={`m360-ver-badge ${String(
                              item.status
                            ).toLowerCase()}`}
                          >
                            {item.status}
                          </span>
                        ) : null}
                      </div>
                      {isSelected && (
                        <Icons.Check
                          size={14}
                          className="m360-version-item-check"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="m360-sidebar-body">
        {/* Classification */}
        <div className="m360-sidebar-section">
        <h4 className="m360-section-label">Classification</h4>
        <div className="m360-detail-row">
          <span className="m360-detail-key">Industry</span>
          <a
            href={sidebarData.classification?.industryLink || "#"}
            className="m360-detail-link"
          >
            {sidebarData.classification?.industry}
          </a>
        </div>
      </div>

      {/* Categories */}
      <div className="m360-sidebar-section">
        <h4 className="m360-section-label">Categories</h4>
        <div className="m360-categories-list">
          {sidebarData.categories?.map((cat, idx) => (
            <span key={idx} className="m360-category-chip">
              {cat}
            </span>
          ))}
        </div>
      </div>

      {/* Sub Categories */}
      <div className="m360-sidebar-section">
        <h4 className="m360-section-label">Sub Categories</h4>
        <span className="m360-detail-plain">{sidebarData.subCategories}</span>
      </div>

      {/* Compatibility & Dependencies */}
      <div className="m360-sidebar-section">
        <h4 className="m360-section-label">Compatibility &amp; Dependencies</h4>
        <div className="m360-detail-row">
          <span className="m360-detail-key">Dependent Machines</span>
          <span className="m360-detail-value">
            {sidebarData.compatibility?.dependentMachines}
          </span>
        </div>
        <div className="m360-detail-row">
          <span className="m360-detail-key">Compatible Machines</span>
          <span className="m360-detail-value">
            {sidebarData.compatibility?.compatibleMachines}
          </span>
        </div>
        <div className="m360-detail-row">
          <span className="m360-detail-key">Version</span>
          <span className="m360-detail-value">
            {sidebarData.compatibility?.version}
          </span>
        </div>
      </div>

      {/* Data Files */}
      <div className="m360-sidebar-section">
        <h4 className="m360-section-label">Data Files</h4>
        {sidebarData.dataFiles?.map((file) => (
          <div key={file.id} className="m360-data-file-item">
            <div className="m360-data-file-top">
              <span className="m360-data-file-name">{file.title}</span>
              <a
                href={file.downloadUrl || "#"}
                className="m360-download-btn"
                title={`Download ${file.title}`}
              >
                <Icons.Download size={12} />
                <span>Download</span>
              </a>
            </div>
            <p className="m360-data-file-desc">{file.description}</p>
          </div>
        ))}
      </div>

      {/* Infrastructure Requirement Card (Tinted Yellow) */}
      <div className="m360-infra-card">
        <h4 className="m360-infra-title">Infrastructure Requirement</h4>
        <div className="m360-infra-row">
          <div className="m360-infra-left">
            <Icons.Linux size={14} className="m360-infra-icon" />
            <span>OS Requirement</span>
          </div>
          <span className="m360-infra-val">
            {sidebarData.infrastructure?.osRequirement}
          </span>
        </div>
        <div className="m360-infra-row">
          <div className="m360-infra-left">
            <Icons.Cpu size={14} className="m360-infra-icon" />
            <span>CPU</span>
          </div>
          <span className="m360-infra-val">
            {sidebarData.infrastructure?.cpu}
          </span>
        </div>
        <div className="m360-infra-row">
          <div className="m360-infra-left">
            <Icons.Ram size={14} className="m360-infra-icon" />
            <span>Memory</span>
          </div>
          <span className="m360-infra-val">
            {sidebarData.infrastructure?.memory}
          </span>
        </div>
        <div className="m360-infra-row">
          <div className="m360-infra-left">
            <Icons.Cloud size={14} className="m360-infra-icon" />
            <span>Cloud</span>
          </div>
          <span className="m360-infra-val">
            {sidebarData.infrastructure?.cloud}
          </span>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="m360-sidebar-section m360-usage-stats-section">
        <h4 className="m360-section-label">Usage Stats</h4>
        <div className="m360-detail-row">
          <span className="m360-detail-key">Total Onboarded</span>
          <span className="m360-detail-value">
            {sidebarData.usageStats?.totalOnboarded}
          </span>
        </div>
        <div className="m360-detail-row">
          <span className="m360-detail-key">Active OnBoarding</span>
          <span className="m360-detail-value">
            {sidebarData.usageStats?.activeOnBoarding}
          </span>
        </div>
      </div>
    </div>
  </aside>
  );
};
